// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { columnChart, fuelDonut, mixBarHtml, fuelLegendHtml, stacked100Area } from '../components/charts';
import { FUEL_COLOUR, FUEL_ORDER, evShare, fuelShare, type Mix } from '../fuels';
import { stateColour } from '../data';
import { glossaryTerm } from '../glossary';
import { formatNumber, formatPercent } from '../utils/format';

export function renderNewCar(root: HTMLElement, ctx: ViewContext): void {
  const { meta, national } = ctx.data;
  const newest = meta.newestCompleteYom;

  // Model-year cohorts available for the slider (drop the ~1-month 2025 sliver
  // and the negligible pre-2005 tail).
  const byYear = new Map<number, Mix>();
  for (const r of national.byYom) byYear.set(r.year, r);
  const years = national.byYom.map((r) => r.year).filter((y) => y >= 2005 && y <= newest).sort((a, b) => a - b);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">The New-Car Effect</h1>
      <p class="view-sub">
        The whole fleet is only ${formatPercent(meta.national.evShare)} ${glossaryTerm('electrified', 'electrified')}.
        But drag the slider from “whole fleet” toward the newest cars and watch the electric and hybrid slices grow —
        that gap between the fleet and the newest cars is the difference between a slow-moving
        ${glossaryTerm('stock', 'stock')} and this year's sales.
      </p>
    </div>

    <div class="panel" style="margin-bottom:var(--space-xl)">
      <div class="range-control">
        <div class="range-row">
          <span class="field-label" style="flex-shrink:0">Cars built in</span>
          <input type="range" min="0" max="${years.length}" value="0" step="1" aria-label="Model year" data-role="slider" />
          <span class="range-value" data-role="slider-label"></span>
        </div>
      </div>
      <div class="hero" style="margin-top:var(--space-lg);margin-bottom:0">
        <div>
          <div class="stat-grid" style="margin-bottom:var(--space-md)">
            <div class="stat"><div class="stat-label">Fully electric</div><div class="stat-value green" data-role="s-bev">—</div></div>
            <div class="stat"><div class="stat-label">Electrified</div><div class="stat-value" style="color:${FUEL_COLOUR.hybrid}" data-role="s-ev">—</div></div>
            <div class="stat"><div class="stat-label">Diesel</div><div class="stat-value diesel" data-role="s-diesel">—</div></div>
            <div class="stat"><div class="stat-label">Cars in view</div><div class="stat-value slate" data-role="s-n">—</div></div>
          </div>
          <div data-role="mixbar"></div>
          ${fuelLegendHtml(FUEL_ORDER)}
          <p class="stat-note" data-role="caption" style="margin-top:var(--space-sm)"></p>
        </div>
        <div class="panel" style="display:flex;align-items:center;justify-content:center;gap:var(--space-lg);flex-wrap:wrap">
          <div data-role="donut" style="flex-shrink:0"></div>
        </div>
      </div>
    </div>

    <div class="panel" style="margin-bottom:var(--space-xl)">
      <div class="panel-title">Fuel mix of cars, by the year they were built</div>
      <div class="panel-sub">Each column is 100% of the cars on the road built that model year. The green (electric) and blue (hybrid) wedge appears only in the last few years — the flow the slider above is sampling. Hover any year.</div>
      <div class="chart-scroll" data-role="area"></div>
    </div>

    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title">How old is the fleet?</div>
        <div class="panel-sub">Passenger cars on the road by year of manufacture. The median car was built in ${meta.national.medianYear ?? '—'} — about ${meta.national.avgAgeYears ?? '—'} years old.</div>
        <div class="chart-scroll" data-role="age"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Newest fleets, oldest fleets</div>
        <div class="panel-sub">Median year of manufacture by state, and each state's electric share.</div>
        <div data-role="states"></div>
      </div>
    </div>
  `;

  const slider = root.querySelector('[data-role="slider"]') as HTMLInputElement;
  const sLabel = root.querySelector('[data-role="slider-label"]') as HTMLElement;
  const donutSlot = root.querySelector('[data-role="donut"]') as HTMLElement;
  const mixbarSlot = root.querySelector('[data-role="mixbar"]') as HTMLElement;
  const caption = root.querySelector('[data-role="caption"]') as HTMLElement;

  const paint = () => {
    const idx = Number(slider.value);
    const whole = idx === 0;
    const year = whole ? null : years[idx - 1];
    const mix = whole ? national.passengerMix : byYear.get(year as number) ?? national.passengerMix;
    sLabel.textContent = whole ? 'the whole fleet' : `${year}`;
    (root.querySelector('[data-role="s-bev"]') as HTMLElement).textContent = formatPercent(fuelShare(mix, 'bev'));
    (root.querySelector('[data-role="s-ev"]') as HTMLElement).textContent = formatPercent(evShare(mix));
    (root.querySelector('[data-role="s-diesel"]') as HTMLElement).textContent = formatPercent(fuelShare(mix, 'diesel'));
    (root.querySelector('[data-role="s-n"]') as HTMLElement).textContent = formatNumber(mix.total);
    mixbarSlot.innerHTML = mixBarHtml(mix, FUEL_ORDER, { tall: true });
    donutSlot.innerHTML = '';
    donutSlot.appendChild(fuelDonut(mix, FUEL_ORDER, { size: 180 }));
    caption.textContent = whole
      ? `Across every car on the road, ${formatPercent(evShare(mix))} is electrified. Now drag right.`
      : `Cars built in ${year} are ${formatPercent(evShare(mix))} electrified — vs ${formatPercent(meta.national.evShare)} for the whole fleet.`;
  };
  slider.addEventListener('input', paint);
  paint();

  // Stacked area (1990 → newest complete).
  const areaData = national.byYom
    .filter((r) => r.year >= 1990 && r.year <= newest && r.total > 0)
    .map((r) => ({ year: r.year, mix: r as Mix }));
  const areaSlot = root.querySelector('[data-role="area"]') as HTMLElement;
  areaSlot.appendChild(
    stacked100Area(areaData, {
      width: 960,
      height: 340,
      annotations: [
        { year: 2019, text: 'First meaningful EV registrations (Tesla Model 3 arrives)' },
        { year: 2022, text: 'BYD and MG arrive; EV share of new cars starts climbing fast' },
      ],
    }),
  );

  // Fleet-age columns.
  const ageSlot = root.querySelector('[data-role="age"]') as HTMLElement;
  const ageData = national.byYom
    .filter((r) => r.year >= 1990 && r.year <= newest)
    .map((r) => ({ id: String(r.year), label: `’${String(r.year).slice(2)}`, value: r.total, colour: 'var(--accent-secondary)', tip: `${r.year}: ${formatNumber(r.total)} cars on the road` }));
  ageSlot.appendChild(
    columnChart(ageData, {
      width: 560,
      height: 260,
      labelEvery: 5,
      valueFormat: (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v))),
      annotations: meta.national.medianYear ? [{ atLabel: String(meta.national.medianYear), text: `Median: cars built in ${meta.national.medianYear} or earlier are half the fleet`, colour: 'var(--accent-primary)' }] : [],
    }),
  );

  // State age + EV bars.
  const statesSlot = root.querySelector('[data-role="states"]') as HTMLElement;
  const ages = national.ageByState.slice().sort((a, b) => (b.medianYear ?? 0) - (a.medianYear ?? 0));
  statesSlot.innerHTML = ages
    .map((a) => {
      const evPct = a.evShare ?? 0;
      return `
      <div style="display:flex;align-items:center;gap:var(--space-md);margin-bottom:10px">
        <div style="width:44px;flex-shrink:0"><span class="pill" style="background:${stateColour(a.st)}">${a.st}</span></div>
        <div style="flex:1;min-width:0">
          <div style="display:flex;justify-content:space-between;font-size:var(--font-size-xs);color:var(--text-tertiary)"><span>median build ${a.medianYear ?? '—'}</span><span>${formatPercent(a.evShare)} electrified</span></div>
          <div class="compare-track" style="margin-top:3px"><div class="compare-fill" style="width:${Math.min(100, evPct / 10 * 100).toFixed(1)}%;background:${FUEL_COLOUR.bev}" data-tip="${a.st}: ${formatPercent(a.evShare)} electrified, ${formatPercent(a.bevShare)} fully electric"></div></div>
        </div>
      </div>`;
    })
    .join('');
}
